import { useState, type ReactNode } from 'react'
import { Building2, Check, X, AlertTriangle, Loader2, ArrowRight, ArrowLeft, RotateCcw, Code2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react'
import { KYB_SCENARIOS } from '@features/dashboards/testing-ground/data'
import type { AnyObj, ChecklistItem } from '../types'
import { StageGuidedShell } from './StageGuidedShell'
import type { GuidedStep } from './StageGuidedShell'
import { KYB_CHECKLIST_ICONS, KYB_CHECKLIST_DESCRIPTIONS, kybOutcomePlainLabel } from './kybExperienceShared'

interface KybGuidedViewProps {
  hasCreds: boolean
  loading: boolean
  pollAttempt: number
  scenarioIdx: number | null
  onSelectPersona: (idx: number) => void
  onRun: () => void
  canRun: boolean
  notFound: boolean
  workflowResult: AnyObj | null
  outcome: string
  legalName: string | null
  ownerCount: number
  customerName: string | null
  checklistItems: ChecklistItem[]
  checklistEnabled: Record<string, boolean>
  onToggleChecklistItem: (id: string) => void
  technicalDetails: ReactNode
}

export function KybGuidedView({
  hasCreds, loading, pollAttempt, scenarioIdx, onSelectPersona, onRun, canRun, notFound,
  workflowResult, outcome, legalName, ownerCount, customerName, checklistItems, checklistEnabled, onToggleChecklistItem, technicalDetails,
}: KybGuidedViewProps) {
  const [showTechnical, setShowTechnical] = useState(false)

  const steps: GuidedStep[] = [
    {
      label: 'Overview',
      render: ({ next }) => (
        <div className="rounded-[18px] p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,.12), rgba(167,139,250,.06))', border: '1px solid rgba(99,102,241,.25)' }}>
          <div className="w-16 h-16 rounded-[18px] flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,.18)' }}>
            <Building2 size={30} className="text-[#4f46e5]" />
          </div>
          <h2 className="text-[22px] font-bold text-[#1e293b]">Business Verification</h2>
          <p className="text-[14px] text-[#4f46e5] mt-2 max-w-md mx-auto leading-relaxed">
            Confirm {customerName ?? 'a customer'}&apos;s business is real, look up who owns and controls it, and screen the
            business itself — all from a business name or ABN.
          </p>
          <button
            onClick={next}
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            Get started <ArrowRight size={15} />
          </button>
        </div>
      ),
    },
    {
      label: 'What to include',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">What should be included?</h3>
            <p className="text-[12px] text-[#475569] mt-1">Tap anything that isn&apos;t needed for this customer to leave it out.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {checklistItems.map((item) => {
              const Icon = KYB_CHECKLIST_ICONS[item.id] ?? Building2
              const active = checklistEnabled[item.id] ?? true
              return (
                <button
                  key={item.id}
                  onClick={() => onToggleChecklistItem(item.id)}
                  className="text-left rounded-[14px] p-4 transition-all"
                  style={active
                    ? { background: '#f8fafc', border: '1px solid rgba(99,102,241,.35)' }
                    : { background: '#f8fafc', border: '1px solid #e5e7eb', opacity: 0.5 }
                  }
                >
                  <Icon size={20} className={active ? 'text-[#4f46e5]' : 'text-[#475569]'} />
                  <p className="text-[13px] font-semibold text-[#1e293b] mt-2">{item.label}</p>
                  <p className="text-[12px] text-[#475569] mt-1 leading-snug">{KYB_CHECKLIST_DESCRIPTIONS[item.id] ?? ''}</p>
                </button>
              )
            })}
          </div>
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white" style={{ background: '#6366f1' }}>
              Next <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Try it',
      render: ({ next, back }) => (
        <div className="space-y-4">
          <div>
            <h3 className="text-[16px] font-semibold text-[#1e293b]">Try it yourself</h3>
            <p className="text-[12px] text-[#475569] mt-1">Pick a real, registered test business to run through verification.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            {KYB_SCENARIOS.map((s, i) => (
              <button
                key={i}
                onClick={() => onSelectPersona(i)}
                className="flex items-center gap-2 px-3 py-2 rounded-[8px] text-[12px] font-medium text-left transition-all"
                style={scenarioIdx === i
                  ? { background: 'rgba(99,102,241,.16)', border: '1px solid rgba(99,102,241,.45)', color: '#1e293b' }
                  : { background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }
                }
              >
                <Building2 size={13} className="shrink-0" /> {s.label}
              </button>
            ))}
          </div>
          <button
            onClick={onRun}
            disabled={!canRun}
            className="w-full py-3 rounded-[10px] text-[14px] font-semibold text-white transition-all disabled:opacity-40"
            style={{ background: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,.5)' }}
          >
            {loading
              ? <span className="flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />{pollAttempt === 0 ? 'Looking up business…' : `Still checking… (${pollAttempt}/20)`}</span>
              : 'Verify this business'}
          </button>
          {!hasCreds && <p className="text-[12px] text-amber-700 text-center">Configure credentials above to try the live demo</p>}
          {notFound && (
            <div className="flex items-start gap-2 p-3 rounded-[9px]" style={{ background: 'rgba(251,146,60,.06)', border: '1px solid rgba(251,146,60,.25)' }}>
              <AlertCircle size={14} className="text-amber-700 shrink-0 mt-0.5" />
              <p className="text-[12px] text-amber-700">No match found for this business — try a different test business.</p>
            </div>
          )}
          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={next} disabled={!workflowResult} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-[9px] text-[13px] font-semibold text-white disabled:opacity-40" style={{ background: '#6366f1' }}>
              See result <ArrowRight size={13} />
            </button>
          </div>
        </div>
      ),
    },
    {
      label: 'Result',
      render: ({ back, restart }) => (
        <div className="space-y-4">
          <div className="rounded-[18px] p-6 flex items-center gap-4" style={{ background: outcome === 'PASS' ? 'rgba(52,211,153,.06)' : outcome === 'FAIL' ? 'rgba(248,113,113,.06)' : 'rgba(251,146,60,.06)', border: `1px solid ${outcome === 'PASS' ? 'rgba(52,211,153,.25)' : outcome === 'FAIL' ? 'rgba(248,113,113,.25)' : 'rgba(251,146,60,.25)'}` }}>
            <div className="w-12 h-12 rounded-[12px] flex items-center justify-center shrink-0" style={{ background: outcome === 'PASS' ? 'rgba(52,211,153,.15)' : outcome === 'FAIL' ? 'rgba(248,113,113,.15)' : 'rgba(251,146,60,.15)' }}>
              {outcome === 'PASS' && <Check size={24} className="text-emerald-700" />}
              {outcome === 'FAIL' && <X size={24} className="text-red-700" />}
              {outcome === 'REFER' && <AlertTriangle size={24} className="text-amber-700" />}
            </div>
            <p className="text-[15px] font-semibold text-[#1e293b]">{workflowResult ? kybOutcomePlainLabel(outcome, legalName, ownerCount) : 'Waiting for a result…'}</p>
          </div>

          <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb' }}>
            <button
              onClick={() => setShowTechnical((s) => !s)}
              className="w-full flex items-center justify-between px-4 py-3 transition-colors hover:bg-[#ffffff]"
              style={{ background: '#f8fafc' }}
            >
              <span className="flex items-center gap-2 text-[12px] font-semibold text-[#475569]">
                <Code2 size={13} /> See how this works technically
              </span>
              {showTechnical ? <ChevronUp size={13} className="text-[#64748b]" /> : <ChevronDown size={13} className="text-[#64748b]" />}
            </button>
            {showTechnical && <div className="p-4 space-y-3" style={{ background: '#ffffff' }}>{technicalDetails}</div>}
          </div>

          <div className="flex justify-between pt-2">
            <button onClick={back} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <ArrowLeft size={13} /> Back
            </button>
            <button onClick={restart} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[9px] text-[13px] font-medium text-[#475569]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <RotateCcw size={13} /> Start over
            </button>
          </div>
        </div>
      ),
    },
  ]

  return <StageGuidedShell steps={steps} />
}
